import { ApolloError } from '@apollo/client';
import { Button, FetchError, Loading } from '../../../../components';
import RiskAppetiteItem from './RiskAppetiteItem';
import { useForm } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import { numberFormatter } from '../../../../utils/numberFormatter';

type RiskAppetiteFormValues = {
  MaxTolerableOutageMto: string | number;
  MaxTolerableOutageRto: string | number;
  RecoveryTimeObjectiveMto: string | number;
  RecoveryTimeObjectiveRto: string | number;
};

type RiskAppetiteProps = {
  mtoMonth?: number;
  mtoRevenue?: number;
  rtoMonth?: number;
  rtoRevenue?: number;
  onFinish?: (data: RiskAppetiteFormValues) => void;
  loading?: boolean;
  type?: 'summary' | 'modal';
};

const RiskAppetite = ({
  mtoMonth,
  mtoRevenue,
  rtoMonth,
  rtoRevenue,
  onFinish,
  type = 'summary',
  loading,
}: RiskAppetiteProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RiskAppetiteFormValues>();

  const getFormattedMonth = (month?: number) => {
    if (month === 0 && type === 'summary') return '--';
    return type === 'summary' ? `${month} months` : month;
  };

  const getFormattedRevenue = (revenue?: number) => {
    if (!revenue || (revenue === 0 && type === 'summary')) return '--';
    return type === 'summary' ? numberFormatter(revenue) : revenue;
  };

  const defaultValues = useMemo(
    () => ({
      MaxTolerableOutageMto: getFormattedMonth(mtoMonth),
      MaxTolerableOutageRto:
        type === 'summary' ? getFormattedRevenue(mtoRevenue) : mtoRevenue,
      RecoveryTimeObjectiveMto: getFormattedMonth(rtoMonth),
      RecoveryTimeObjectiveRto:
        type === 'summary' ? getFormattedRevenue(rtoRevenue) : rtoRevenue,
    }),
    [mtoMonth, mtoRevenue, rtoMonth, rtoRevenue, type],
  );

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  if (loading) return <Loading />;

  return (
    <div className="h-full flex flex-col gap-3.5 mt-5">
      <form onSubmit={handleSubmit(onFinish!)}>
        <div
          className={`flex flex-col gap-2 ${type === 'summary' ? 'mr-9' : ''}`}
        >
          <RiskAppetiteItem
            label="Max Tolerable Outage"
            type={type}
            showColumnLabels
            highlightFirst
            register={register}
            errors={errors}
          />
          <RiskAppetiteItem
            label="Recovery Time Objective"
            type={type}
            highlightSecond
            register={register}
            errors={errors}
          />
        </div>

        {type === 'modal' && (
          <div className="flex justify-end mt-4">
            <Button
              htmlType="submit"
              type="primary"
              className="text-sm min-w-[180px] h-6"
            >
              Save
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default RiskAppetite;
